import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { aggiungiGiorni, isoGiorno, messaggioErrore } from '../../utils/agenda';
import { adessoRoma, linkChiama } from '../../utils/percorso';
import {
  DURATA_BASE, DURATE, RIGHE, blocchiDa, blocchiVisiteCliente, disponibile, dispVuota, etichettaGiorno, etichettaImmobile,
  giorniSettimana, hhmm, intervallo, lunediDi, minutiDa, passato, slotConsigliati, sovrapposte, testoBreve, testoSlot,
  INIZIO, PASSO, FINE, durataFila, filaSiAccavalla, orariInFila, titoloFila,
} from '../../utils/fissaVisita';
import { usePersone } from './Persone';
import Icona from '../cliente/Icone';
import './fissa-visita.css';

// "Fissa visita" (scheda cliente, scheda immobile, esito "Visita fissata", agenda, "Dai clienti"): finestra grande con
// a sinistra il calendario della settimana (8-20, mezz'ora per riga) e le attività di chi accompagna; le fasce in cui
// il cliente non è disponibile in rosso tenue; le sue visite in evidenza; in alto gli slot consigliati. Un clic su
// uno slot libero apre l'anteprima con immobile, agenzia, chi accompagna, durata, nota per il cliente → "Fissa e
// avvisa" (POST /clienti/{id}/visite: email e push al cliente, email all'agenzia se ce l'ha, agenda, cronologia).
// invia = un altro modo di salvare (l'esito della telefonata: POST /telefonate/esito con esito "visita_fissata").

const GIORNI_TESTA = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
const ALTEZZA = 26;  // px per mezz'ora
const OGNI_MEZZO_MINUTO = 30000;

// Quanto della "Nota per il cliente" il server tiene davvero: mille caratteri sia fissando una visita nuova
// (POST /clienti/{id}/visite → `note_prima`, backend/server.py) sia spostandola (POST /visite/{id}/sposta,
// backend/visite_staff.py) — è la stessa nota nello stesso campo, quindi lo stesso limite: chi ne scriveva 800
// fissando e poi spostava ne perdeva 300. L'esito "Visita fissata" passa invece dalla nota della telefonata
// (POST /telefonate/esito, backend/telefonate.py), che si ferma a 500 come gli altri campi di quel riquadro.
// Il campo non aveva nessun limite: il server tagliava in silenzio e l'email al cliente partiva mozzata a metà parola.
export const MAX_NOTA_FISSA = 1000;
export const MAX_NOTA_ESITO = 500;
export const maxNota = ({ sposta = null, invia = null } = {}) => (invia && !sposta ? MAX_NOTA_ESITO : MAX_NOTA_FISSA);

// La settimana intera chiede almeno 640 px (48 px di orari + sette colonne): a 390 px la griglia scorreva dentro il
// suo riquadro e si vedevano tre giorni e mezzo e due fasce orarie — restava utile solo la riga degli slot
// consigliati. Sul telefono si guarda un giorno alla volta, con le frecce: la colonna sta tutta nella larghezza e
// scorre solo in su e in giù. Il taglio è quello del CSS (fissa-visita.css, @media max-width:700px).
export const TELEFONO = '(max-width: 700px)';
export const useTelefono = () => {
  const [telefono, setTelefono] = useState(() => Boolean(window.matchMedia && window.matchMedia(TELEFONO).matches));
  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia(TELEFONO);
    if (!mq) return undefined;
    const cambia = () => setTelefono(mq.matches);
    cambia();
    // i browser vecchi hanno solo addListener
    if (mq.addEventListener) { mq.addEventListener('change', cambia); return () => mq.removeEventListener('change', cambia); }
    mq.addListener(cambia);
    return () => mq.removeListener(cambia);
  }, []);
  return telefono;
};

// sposta = {id, data, ora, durata, accompagna_id}: la finestra si apre sullo slot della visita e la sposta
// caseInSequenza = [{id, titolo, indirizzo}] (almeno due, della stessa agenzia): "tre case, stesso pomeriggio".
// Scelto lo slot iniziale, l'anteprima propone la fila (15:00 · via Manzoni 14 — 16:00 · via Cavour 3 — …), si può
// togliere una casa o cambiare la durata (gli orari si rifanno) e si manda UNA POST /clienti/{id}/visite-in-sequenza,
// così il cliente riceve una email e una notifica sole. Senza la prop non cambia niente: una visita, come sempre.
const FissaVisita = ({ clienteId: clienteIniziale = null, clienteNome = '', immobileId: immobileIniziale = '', immobili: immobiliDati = null,
  onChiudi, onFatto, invia = null, sposta = null, titolo: titoloDato = null, caseInSequenza = null }) => {
  const titolo = titoloDato || (sposta ? 'Sposta la visita' : 'Fissa una visita');
  const { persone, ioId } = usePersone();
  // "Adesso" nell'ora di Roma, come le date delle visite: con il telefono su un altro fuso la settimana si apriva sul
  // giorno sbagliato e gli slot già passati non erano quelli giusti. L'orologio si rilegge ogni mezzo minuto e cambia
  // solo al cambio di minuto (o di giorno), come il riquadro dell'esito: con la finestra lasciata aperta a lungo gli
  // slot che intanto passano si spengono da soli, invece di restare accesi e far fissare una visita nel passato.
  const [adesso, setAdesso] = useState(adessoRoma);
  useEffect(() => {
    const t = setInterval(() => setAdesso((prec) => {
      const ora = adessoRoma();
      return ora.getMinutes() === prec.getMinutes() && ora.getDate() === prec.getDate() ? prec : ora;
    }), OGNI_MEZZO_MINUTO);
    return () => clearInterval(t);
  }, []);
  const oggi = isoGiorno(adesso);
  const [clienteId, setClienteId] = useState(clienteIniziale);
  const [nome, setNome] = useState(clienteNome);
  const [clienti, setClienti] = useState([]);
  const [immobili, setImmobili] = useState(immobiliDati || []);
  const [immobileId, setImmobileId] = useState(immobileIniziale || '');
  const [accompagna, setAccompagna] = useState(sposta?.accompagna_id || '');
  const [lunedi, setLunedi] = useState(lunediDi(sposta?.data || oggi));
  // sul telefono si vede un giorno solo: quale
  const telefono = useTelefono();
  const [giornoUno, setGiornoUno] = useState(sposta?.data || oggi);
  // il giorno guardato e la settimana caricata restano insieme (le attività di chi accompagna arrivano per settimana)
  const vaAlGiorno = (iso) => { setGiornoUno(iso); setLunedi(lunediDi(iso)); };
  const [voci, setVoci] = useState([]);
  const [prossimi, setProssimi] = useState([]);
  const [disp, setDisp] = useState(null);
  const [visite, setVisite] = useState([]);
  const [durata, setDurata] = useState(sposta?.durata && [30, 45, 60, 90].includes(Number(sposta.durata)) ? Number(sposta.durata) : DURATA_BASE);
  const [nota, setNota] = useState('');
  const limiteNota = maxNota({ sposta, invia });
  const [scelto, setScelto] = useState(sposta?.data && sposta?.ora ? { data: sposta.data, inizio: minutiDa(sposta.ora), fuori: false } : null);  // {data, inizio, fuori}
  const [rosso, setRosso] = useState(null);  // slot fuori dalla disponibilità, in attesa di "Fissa comunque"
  const [confermaSovrapposto, setConfermaSovrapposto] = useState(false);
  const [attesa, setAttesa] = useState(false);
  const [tolte, setTolte] = useState([]);  // le case tolte dalla fila

  // "Tre case, stesso pomeriggio": solo con la prop, e mai insieme a "Sposta" (lì la visita è una sola) o a
  // "invia" (l'esito della telefonata salva una visita per volta).
  const caseFila = useMemo(
    () => (caseInSequenza || []).filter((c) => c?.id && !tolte.includes(c.id)),
    [caseInSequenza, tolte],
  );
  const inFila = !sposta && !invia && (caseInSequenza || []).length >= 2 && caseFila.length >= 2;

  useEffect(() => { if (!accompagna && ioId) setAccompagna(ioId); }, [ioId, accompagna]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onChiudi(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onChiudi]);
  // senza cliente (agenda): si sceglie dalla lista
  useEffect(() => {
    if (clienteIniziale) return;
    axios.get(`${API}/clienti-lista`).then((r) => setClienti(r.data.righe || [])).catch(() => setClienti([]));
  }, [clienteIniziale]);
  // la disponibilità e le visite si rileggono ogni volta che si apre (sono quelle di adesso)
  useEffect(() => {
    if (!clienteId) { setDisp(null); setVisite([]); return; }
    axios.get(`${API}/clienti/${clienteId}/disponibilita`).then((r) => setDisp(r.data.disponibilita || null)).catch(() => setDisp(null));
    axios.get(`${API}/clienti/${clienteId}/visite`).then((r) => setVisite(Array.isArray(r.data) ? r.data : [])).catch(() => setVisite([]));
  }, [clienteId]);
  const conImmobili = Boolean(immobiliDati);
  useEffect(() => {
    if (!clienteId || conImmobili) return;
    axios.get(`${API}/clienti/${clienteId}/immobili`).then((r) => {
      const attivi = (r.data || []).filter((i) => i.stato_attivita !== 'scartato');
      setImmobili(attivi);
      setImmobileId((id) => id || (attivi.length === 1 ? attivi[0].id : ''));
    }).catch(() => setImmobili([]));
  }, [clienteId, conImmobili]);
  // il nome del cliente, se non arriva da chi apre (scheda immobile)
  useEffect(() => {
    if (!clienteIniziale || clienteNome) return;
    axios.get(`${API}/clienti/${clienteIniziale}`).then((r) => setNome([r.data.nome, r.data.cognome].filter(Boolean).join(' '))).catch(() => {});
  }, [clienteIniziale, clienteNome]);
  useEffect(() => {
    if (!accompagna) return;
    axios.get(`${API}/agenda`, { params: { dal: lunedi, al: aggiungiGiorni(lunedi, 6), persona: accompagna } })
      .then((r) => setVoci(r.data.voci || [])).catch(() => setVoci([]));
  }, [lunedi, accompagna]);
  useEffect(() => {
    if (!accompagna) return;
    axios.get(`${API}/agenda`, { params: { dal: oggi, al: aggiungiGiorni(oggi, 10), persona: accompagna } })
      .then((r) => setProssimi(r.data.voci || [])).catch(() => setProssimi([]));
  }, [oggi, accompagna]);

  // la visita che si sta spostando non conta come impegno
  const visiteCliente = useMemo(() => blocchiVisiteCliente(visite, nome).filter((b) => b.visitaId !== sposta?.id), [visite, nome, sposta]);
  const mieBlocchi = useMemo(() => blocchiDa(voci).filter((b) => !sposta || b.visitaId !== sposta.id), [voci, sposta]);
  // nel calendario: le mie attività, più le visite del cliente con altri (senza doppioni)
  const blocchi = useMemo(() => {
    const mie = new Set(mieBlocchi.map((b) => b.visitaId).filter(Boolean));
    return [...mieBlocchi.map((b) => ({ ...b, delCliente: Boolean(b.visitaId && visiteCliente.some((c) => c.visitaId === b.visitaId)) })),
      ...visiteCliente.filter((c) => !mie.has(c.visitaId))];
  }, [mieBlocchi, visiteCliente]);
  // in fila lo slot deve bastare a tutte le visite, pause comprese
  const durataTotale = inFila ? durataFila(caseFila.length, durata) : durata;
  // Senza cliente niente slot consigliati: erano gli orari liberi di chi accompagna, e cambiavano appena si sceglieva
  // il cliente (la sua disponibilità). Ora finché non c'è il cliente si legge solo "Scegliete il cliente".
  const consigliati = useMemo(() => (clienteId ? slotConsigliati({
    adesso, disp, durata: durataTotale,
    blocchi: [...blocchiDa(prossimi).filter((b) => !sposta || b.visitaId !== sposta.id), ...visiteCliente],
  }) : []), [adesso, disp, clienteId, durataTotale, prossimi, visiteCliente, sposta]);

  const giorni = telefono ? [giornoUno] : giorniSettimana(lunedi);
  const immobile = immobili.find((i) => i.id === immobileId) || null;
  const agenzia = immobile?.agenzia_info;
  const tel = linkChiama(agenzia?.telefono);
  // gli orari della fila, rifatti a ogni cambio di durata o di case
  const orari = scelto && inFila ? orariInFila(scelto.inizio, caseFila.length, durata) : [];
  // sovrapposizioni: le vostre attività e le altre visite del cliente (in fila: per ogni orario, senza doppioni)
  const conflitti = !scelto ? [] : inFila
    ? orari.reduce((acc, m) => [...acc, ...sovrapposte(blocchi, scelto.data, m, durata).filter((b) => !acc.some((x) => x.id === b.id))], [])
    : sovrapposte(blocchi, scelto.data, scelto.inizio, durata);
  // la fila si mostra anche fuori dalla disponibilità del cliente: si dice quali orari, e si chiede conferma
  const oreFuori = scelto && inFila && clienteId ? orari.filter((m) => !disponibile(disp, scelto.data, m)) : [];
  const fuori = Boolean(scelto?.fuori) || oreFuori.length > 0;
  const oltreSera = inFila && orari.length > 0 && orari[orari.length - 1] + durata > FINE;
  const accavallate = inFila && filaSiAccavalla(orari, durata);
  const chi = persone.find((p) => p.id === accompagna);
  // lo slot scelto può passare mentre la finestra resta aperta: il server rifiuta una visita nel passato (400), e un
  // bottone che non può funzionare non si mostra acceso — l'anteprima lo dice e si sceglie un altro orario
  const sceltoPassato = Boolean(scelto) && passato(scelto.data, scelto.inizio, adesso);

  const tocca = (iso, min) => {
    if (passato(iso, min, adesso)) return;
    setConfermaSovrapposto(false);
    if (clienteId && !disponibile(disp, iso, min)) { setRosso({ data: iso, inizio: min }); setScelto(null); return; }
    setRosso(null);
    setScelto({ data: iso, inizio: min, fuori: false });
  };
  const scegliConsigliato = (s) => { vaAlGiorno(s.data); setRosso(null); setConfermaSovrapposto(false); setScelto({ data: s.data, inizio: s.inizio, fuori: false }); };

  const fissa = async () => {
    // uno slot già passato non si manda: il server lo rifiuta con un 400 (blocco 2)
    if (!scelto || !clienteId || passato(scelto.data, scelto.inizio, adessoRoma())) return;
    if (!inFila && !immobileId) { toast.error('Scegliete l\'immobile'); return; }
    // i conflitti sono calcolati sui dati letti all'apertura: quelli dentro la fila si vedono qui, prima del 409
    if (accavallate) { toast.error('Due visite si accavallano: cambiate un orario'); return; }
    if (oltreSera) { toast.error('L\'ultima visita finirebbe dopo le 20:00'); return; }
    if (conflitti.length && !confermaSovrapposto) { setConfermaSovrapposto(true); return; }
    const dati = { immobile_id: immobileId, data: scelto.data, ora: hhmm(scelto.inizio), accompagna_id: accompagna || undefined,
      durata_minuti: durata, nota: nota.trim().slice(0, limiteNota) || undefined };
    setAttesa(true);
    try {
      const quando = `${scelto.data}T${hhmm(scelto.inizio)}:00`;
      let r;
      if (inFila) {
        r = (await axios.post(`${API}/clienti/${clienteId}/visite-in-sequenza`, {
          visite: caseFila.map((c, n) => ({ immobile_id: c.id, data_visita: `${scelto.data}T${hhmm(orari[n])}:00`, durata_minuti: durata })),
          accompagna_id: dati.accompagna_id, note_prima: dati.nota })).data;
        toast.success(r?.testo || `${caseFila.length} visite fissate, una dopo l'altra`);
        if (onFatto) onFatto(r);
        return;
      }
      if (invia) r = await invia(dati);
      else if (sposta) {
        r = (await axios.post(`${API}/visite/${sposta.id}/sposta`, { data_visita: quando, durata_minuti: durata, accompagna_id: dati.accompagna_id,
          ...(dati.nota ? { nota: dati.nota } : {}) })).data;
      } else {
        r = (await axios.post(`${API}/clienti/${clienteId}/visite`, { immobile_id: immobileId, data_visita: quando, accompagna_id: dati.accompagna_id,
          durata_minuti: durata, note_prima: dati.nota })).data;
      }
      toast.success(`Visita ${sposta ? 'spostata' : 'fissata'}: ${testoSlot(scelto.data, scelto.inizio, durata)}${r?.conferma_agenzia || r?.agenzia_avvisata ? ' · agenzia avvisata' : ''}`);
      if (onFatto) onFatto(r);
    } catch (e) {
      // chi sta spostando non sta fissando: il fallito diceva "Visita non fissata" anche lì (la riga del successo,
      // qui sopra, la distinzione la fa già)
      toast.error(messaggioErrore(e, `Visita non ${sposta ? 'spostata' : 'fissata'}. Riprovate.`));
      setAttesa(false);
    }
  };

  const avvisoConflitto = conflitti[0];
  // "Sposta e avvisa" con lo stesso giorno, la stessa ora, la stessa durata e lo stesso accompagnatore non sposta
  // niente: mandava comunque email e push al cliente e all'agenzia per dire che la visita era stata spostata
  // (e il server la rifiuterebbe). Resta spento finché non cambia qualcosa.
  const invariato = Boolean(sposta) && Boolean(scelto) && scelto.data === sposta.data && hhmm(scelto.inizio) === sposta.ora
    && Number(durata) === Number(sposta.durata || DURATA_BASE) && (accompagna || '') === (sposta.accompagna_id || '');
  return createPortal(
    <div className="st st-modale fissa-modale" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
      <div className="pannello" role="dialog" aria-modal="true" aria-labelledby="fv-titolo" data-testid="fissa-visita">
        <header className="fv-testa">
          <div style={{ minWidth: 0 }}>
            <span className="k">Agenda di {chi?.nome || '…'}</span>
            <h2 id="fv-titolo">{titolo}{nome ? <> · <em>{nome}</em></> : null}</h2>
          </div>
          <div className="fv-scelte">
            {!clienteIniziale && (
              <select value={clienteId || ''} aria-label="Cliente" onChange={(e) => {
                const c = clienti.find((x) => x.id === e.target.value);
                setClienteId(e.target.value || null); setNome(c ? c.nome : ''); setImmobileId(''); setScelto(null); setRosso(null);
              }}>
                <option value="">Quale cliente?</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            )}
            {clienteId && !sposta && !inFila && (
              <select value={immobileId} aria-label="Immobile" onChange={(e) => setImmobileId(e.target.value)}>
                <option value="">Quale immobile?</option>
                {/* mai una riga vuota: un immobile ancora senza indirizzo né titolo si legge lo stesso */}
                {immobili.map((i) => <option key={i.id} value={i.id}>{etichettaImmobile(i)}</option>)}
              </select>
            )}
            <button type="button" className="x" onClick={onChiudi} aria-label="Chiudi">✕</button>
          </div>
        </header>

        <div className="fv-consigliati" data-testid="slot-consigliati">
          <span className="k">Slot consigliati</span>
          {consigliati.length === 0 && <span className="muto">{clienteId ? 'Nessuno slot libero per entrambi nei prossimi 10 giorni.' : 'Scegliete il cliente.'}</span>}
          {consigliati.map((s) => (
            <button type="button" key={`${s.data}-${s.inizio}`} className={`chip${scelto?.data === s.data && scelto?.inizio === s.inizio ? ' on' : ''}`}
              onClick={() => scegliConsigliato(s)}>{testoBreve(s.data, s.inizio)}</button>
          ))}
          {clienteId && dispVuota(disp) && <span className="muto">· il cliente non ha segnato la sua disponibilità</span>}
        </div>

        <div className="fv-corpo">
          <section className="fv-calendario" aria-label="Calendario della settimana">
            {/* sul telefono le frecce fanno un giorno alla volta (la settimana non ci sta in larghezza) */}
            <div className="fv-nav">
              <button type="button" className="btn sm" onClick={() => (telefono ? vaAlGiorno(aggiungiGiorni(giornoUno, -1)) : setLunedi(aggiungiGiorni(lunedi, -7)))}
                aria-label={telefono ? 'Giorno prima' : 'Settimana prima'}>‹</button>
              <b data-testid="fv-quando">{telefono ? etichettaGiorno(giornoUno) : intervallo(lunedi)}</b>
              <button type="button" className="btn sm" onClick={() => (telefono ? vaAlGiorno(aggiungiGiorni(giornoUno, 1)) : setLunedi(aggiungiGiorni(lunedi, 7)))}
                aria-label={telefono ? 'Giorno dopo' : 'Settimana dopo'}>›</button>
              {telefono
                ? giornoUno !== oggi && <button type="button" className="link" onClick={() => vaAlGiorno(oggi)}>Oggi</button>
                : lunedi !== lunediDi(oggi) && <button type="button" className="link" onClick={() => setLunedi(lunediDi(oggi))}>Questa settimana</button>}
              <span className="fv-legenda"><i className="lg-no" />il cliente non c'è<i className="lg-cl" />sue visite<i className="lg-mie" />vostre attività</span>
            </div>
            <div className="fv-scorri">
              <div className={`fv-griglia${telefono ? ' uno' : ''}`} style={{ '--h': `${ALTEZZA}px` }} data-testid="fv-griglia">
                <div className="fv-ore">
                  <div className="fv-gtesta" />
                  {RIGHE.map((m) => <div key={m} className="fv-ora">{m % 60 === 0 ? hhmm(m) : ''}</div>)}
                </div>
                {giorni.map((iso) => {
                  const d = new Date(`${iso}T12:00:00`);
                  const delGiorno = blocchi.filter((b) => b.data === iso && b.fine > INIZIO);
                  const tuttoIlGiorno = voci.filter((v) => v.data === iso && (v.tutto_il_giorno || !v.ora) && v.stato === 'da_fare');
                  return (
                    <div key={iso} className={`fv-giorno${iso === oggi ? ' oggi' : ''}`} data-testid={`giorno-${iso}`}>
                      <div className="fv-gtesta"><b>{GIORNI_TESTA[d.getDay()]} {d.getDate()}</b>
                        {tuttoIlGiorno.length > 0 && <small title={tuttoIlGiorno.map((v) => v.cosa?.azione || v.titolo).join(' · ')}>+{tuttoIlGiorno.length} in giornata</small>}</div>
                      <div className="fv-celle">
                        {RIGHE.map((m) => {
                          const vecchio = passato(iso, m, adesso);
                          const no = Boolean(clienteId) && !disponibile(disp, iso, m);
                          const sel = scelto && scelto.data === iso && m >= scelto.inizio && m < scelto.inizio + durataTotale;
                          return (
                            <button type="button" key={m} className={['fv-cella', vecchio ? 'passato' : '', no ? 'no' : '', sel ? 'scelta' : '', rosso && rosso.data === iso && rosso.inizio === m ? 'avviso' : ''].filter(Boolean).join(' ')}
                              disabled={vecchio} onClick={() => tocca(iso, m)} data-testid={`slot-${iso}-${hhmm(m)}`}
                              aria-label={`${GIORNI_TESTA[d.getDay()]} ${d.getDate()} ${hhmm(m)}${no ? ' · il cliente non ha segnato disponibilità' : ''}`} />
                          );
                        })}
                        {delGiorno.map((b) => (
                          <div key={b.id} className={`fv-blocco t-${b.tipo}${b.delCliente ? ' del-cliente' : ''}`} data-testid={`blocco-${b.id}`}
                            style={{ top: `${((Math.max(b.inizio, INIZIO) - INIZIO) / PASSO) * ALTEZZA}px`, height: `${Math.max(((Math.min(b.fine, INIZIO + RIGHE.length * PASSO) - Math.max(b.inizio, INIZIO)) / PASSO) * ALTEZZA - 2, 14)}px` }}>
                            <b>{b.ora}</b> {b.titolo}{b.cliente ? ` · ${b.cliente}` : ''}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <aside className={`fv-anteprima${scelto || rosso ? ' aperta' : ''}`} aria-live="polite">
            {rosso && !scelto && (
              <div className="fv-avviso rosso" data-testid="avviso-disponibilita">
                <b>{testoSlot(rosso.data, rosso.inizio, durataTotale)}</b>
                <p>Il cliente non ha segnato disponibilità qui.</p>
                <div className="bottoni">
                  <button type="button" className="btn sm" onClick={() => setRosso(null)}>Scegli un altro slot</button>
                  <button type="button" className="btn sm rosso-vuoto" onClick={() => { setScelto({ ...rosso, fuori: true }); setRosso(null); }}>{sposta ? 'Sposta comunque' : 'Fissa comunque'}</button>
                </div>
              </div>
            )}
            {!scelto && !rosso && (
              <div className="fv-vuota">
                <Icona nome="calendario" />
                <p>{clienteId ? 'Toccate uno slot libero nel calendario, o uno di quelli consigliati.' : 'Scegliete il cliente: le fasce in cui non c\'è si colorano di rosso.'}</p>
              </div>
            )}
            {scelto && (
              <div className="fv-dettaglio" data-testid="anteprima-visita">
                <span className="k">Anteprima</span>
                <h3>{inFila ? titoloFila(caseFila.length, scelto.inizio) : testoSlot(scelto.data, scelto.inizio, durata)}</h3>
                {fuori && !sceltoPassato && (
                  <p className="fv-nota rosso">Fuori dalla disponibilità del cliente{oreFuori.length ? ` (${oreFuori.map(hhmm).join(', ')})` : ''}.</p>
                )}
                {sceltoPassato && (
                  <p className="fv-nota rosso" data-testid="slot-passato">
                    Quest'ora è passata: scegliete uno slot più avanti (una visita nel passato il server non la accetta).
                  </p>
                )}
                {oltreSera && <p className="fv-nota ambra" data-testid="avviso-sera">L'ultima visita finirebbe dopo le 20:00: togliete una casa o accorciate la durata.</p>}
                {avvisoConflitto && (
                  <p className="fv-nota ambra" data-testid="avviso-sovrapposizione">
                    Hai già {avvisoConflitto.titolo}{avvisoConflitto.cliente ? ` (${avvisoConflitto.cliente})` : ''} alle {avvisoConflitto.ora}
                    {conflitti.length > 1 ? ` e altre ${conflitti.length - 1}` : ''}.
                  </p>
                )}
                {inFila ? (
                  <ol className="fv-fila" data-testid="fila-sequenza">
                    {caseFila.map((c, n) => (
                      <li key={c.id} data-testid={`fila-${c.id}`}>
                        <b>{hhmm(orari[n])}</b> · <span>{c.indirizzo || c.titolo}</span>
                        <button type="button" className="via" disabled={caseFila.length <= 2} onClick={() => setTolte((t) => [...t, c.id])}
                          aria-label={`Togli ${c.indirizzo || c.titolo} dalla fila`}
                          title={caseFila.length <= 2 ? 'Una fila è di almeno due case' : 'Togli dalla fila'}>✕</button>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <dl>
                    <dt>Immobile</dt>
                    <dd>{immobile ? (immobile.titolo || etichettaImmobile(immobile)) : <span className="muto">scegliete l'immobile in alto</span>}</dd>
                    {immobile && (
                      <>
                        <dt>Agenzia</dt>
                        <dd>{agenzia ? <>{agenzia.etichetta || agenzia.nome}{agenzia.contatto ? ` · ${agenzia.contatto}` : ''}{agenzia.telefono ? <> · {tel ? <a href={tel}>{agenzia.telefono}</a> : agenzia.telefono}</> : null}</> : (immobile.agenzia || '—')}</dd>
                      </>
                    )}
                  </dl>
                )}
                {inFila && tolte.length > 0 && (
                  <button type="button" className="link fv-rimetti" onClick={() => setTolte([])}>Rimetti {tolte.length === 1 ? 'la casa tolta' : 'le case tolte'}</button>
                )}
                <label>Chi accompagna
                  <select value={accompagna} onChange={(e) => setAccompagna(e.target.value)} aria-label="Chi accompagna">
                    {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                </label>
                <label>Durata
                  <select value={durata} onChange={(e) => setDurata(Number(e.target.value))} aria-label="Durata">
                    {DURATE.map((m) => <option key={m} value={m}>{m} min</option>)}
                  </select>
                </label>
                <label>Nota per il cliente
                  {/* il campo si ferma a quello che il server tiene: oltre, tagliava lui in silenzio e l'email
                      partiva con la nota mozzata (il taglio vale anche per il testo incollato da fuori) */}
                  <textarea value={nota} onChange={(e) => setNota(e.target.value.slice(0, limiteNota))} rows={2} maxLength={limiteNota}
                    placeholder="es. portate un documento d'identità" aria-label="Nota per il cliente" />
                </label>
                {/* spostando, l'agenzia riceve l'email dello spostamento, non quella di conferma di una visita nuova */}
                <p className="muto fv-effetti">{inFila
                  ? 'Il cliente riceve una email e una notifica sole con tutti gli orari in fila; le visite vanno in agenda e in cronologia.'
                  : sposta
                    ? "Il cliente riceve email e push con il nuovo orario; l'agenzia l'email dello spostamento se ce l'ha; agenda, calendario e cronologia si aggiornano."
                    : "Il cliente riceve email e push; l'agenzia l'email di conferma se ce l'ha; la visita va in agenda e in cronologia."}</p>
                {invariato && <p className="muto fv-effetti" data-testid="niente-da-spostare">Non è cambiato niente: scegliete un altro orario, o cambiate durata o accompagnatore.</p>}
                <div className="bottoni">
                  <button type="button" className="btn sm" onClick={() => { setScelto(null); setConfermaSovrapposto(false); }}>Annulla</button>
                  <button type="button" className={`btn sm ${conflitti.length && !confermaSovrapposto ? 'rosso-vuoto' : 'p'}`} onClick={fissa}
                    disabled={attesa || (inFila ? oltreSera : !immobileId) || invariato || sceltoPassato}>
                    {attesa ? 'Salvo…' : conflitti.length && !confermaSovrapposto ? (sposta ? 'Sposta comunque' : 'Fissa comunque')
                      : sposta ? 'Sposta e avvisa' : inFila ? `Fissa le ${caseFila.length} visite`
                        : conflitti.length || fuori ? 'Sì, fissa e avvisa' : 'Fissa e avvisa'}
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>,
    document.querySelector('.mk-staff') || document.body,
  );
};

export default FissaVisita;
